import { Component, inject, OnInit, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmpresaService } from '../../core/services/empresa.service';
import { AlertService } from '../../core/services/alert.service';
import { HorarioApi } from '../../core/models/api/empresa-api';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { MapPickerButtonComponent } from '../../shared/components/map-picker-dialog-component/mapPickerButtonComponent';
import { MapPickerResult } from '../../services/mapPickerService-service';

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

@Component({
    selector: 'app-admin-taller',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent, MapPickerButtonComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Taller / retiro</h2>
      <p class="text-sm text-gray-400 mt-1">Dirección, mapa y horario que ve el cliente al elegir retiro.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <form *ngIf="!loading()" class="flex flex-col gap-6" (ngSubmit)="guardarTaller()">
      <section class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
        <label class="md:col-span-2 block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre del taller</span>
          <input [ngModel]="nombre()" (ngModelChange)="nombre.set($event)" name="nombre" required class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="md:col-span-2 block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Dirección</span>
          <div class="mt-1 flex gap-2">
            <input [ngModel]="direccion()" (ngModelChange)="direccion.set($event)" name="direccion" required class="flex-1 rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
            <app-map-picker-button mode="search" [value]="mapaValue()" buttonLabel="Elegir en mapa" editLabel="Cambiar en mapa" (selected)="onTallerLocation($event)"></app-map-picker-button>
          </div>
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Distrito</span>
          <input [ngModel]="distrito()" (ngModelChange)="distrito.set($event)" name="distrito" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Teléfono</span>
          <input [ngModel]="telefono()" (ngModelChange)="telefono.set($event)" name="telefono" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Latitud</span>
          <input [ngModel]="latitud()" (ngModelChange)="latitud.set($event)" name="latitud" type="number" step="0.000001" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Longitud</span>
          <input [ngModel]="longitud()" (ngModelChange)="longitud.set($event)" name="longitud" type="number" step="0.000001" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="md:col-span-2 block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Link de Google Maps</span>
          <input [ngModel]="urlMaps()" (ngModelChange)="urlMaps.set($event)" name="urlMaps" placeholder="https://maps.app.goo.gl/..." class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <button [disabled]="savingTaller()" class="md:col-span-2 py-3.5 rounded-2xl text-white font-bold text-sm disabled:opacity-60" style="background-color: var(--bg-main);">
          {{ savingTaller() ? 'Guardando...' : 'Guardar taller' }}
        </button>
      </section>
    </form>

    <section *ngIf="!loading()" class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm mt-6">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p class="text-sm font-black text-gray-800">Horario de atención</p>
          <p class="text-xs text-gray-400">Ajusta los días que necesites y guarda todo con un solo botón.</p>
        </div>
        <button type="button" (click)="guardarTodo()" [disabled]="guardandoTodo()" class="px-6 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">{{ guardandoTodo() ? 'Guardando…' : 'Guardar horario' }}</button>
      </div>
      <div class="flex flex-col gap-3">
        <div *ngFor="let h of horarios()" class="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          <p class="md:col-span-2 text-sm font-semibold text-gray-700">{{ diaNombre(h.diaSemana) }}</p>
          <label class="md:col-span-2 flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" [checked]="h.esCerrado" (change)="toggleCerrado(h)" class="accent-rose-500" /> Cerrado
          </label>
          <input [ngModel]="toInput(h.horaInicio)" (ngModelChange)="setHora(h, 'horaInicio', $event)" [name]="'ini'+h.diaSemana" type="time" [disabled]="h.esCerrado" class="md:col-span-3 rounded-2xl border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50" />
          <input [ngModel]="toInput(h.horaFin)" (ngModelChange)="setHora(h, 'horaFin', $event)" [name]="'fin'+h.diaSemana" type="time" [disabled]="h.esCerrado" class="md:col-span-3 rounded-2xl border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50" />
          <span class="md:col-span-2"></span>
        </div>
      </div>
    </section>
    `
})
export class AdminTallerComponent implements OnInit {
    private empresaService = inject(EmpresaService);
    private alerts = inject(AlertService);

    loading = signal(true);
    savingTaller = signal(false);
    nombre = signal('');
    direccion = signal('');
    distrito = signal('');
    telefono = signal('');
    latitud = signal<number | null>(null);
    longitud = signal<number | null>(null);
    urlMaps = signal('');
    horarios = signal<HorarioApi[]>([]);
    guardandoTodo = signal(false);

    ngOnInit(): void {
        this.empresaService.getTaller().subscribe({
            next: (t) => {
                if (t) {
                    this.nombre.set(t.nombre);
                    this.direccion.set(t.direccion);
                    this.distrito.set(t.distrito ?? '');
                    this.telefono.set(t.telefono ?? '');
                    this.latitud.set(t.latitud);
                    this.longitud.set(t.longitud);
                    this.urlMaps.set(t.urlMaps ?? '');
                    this.horarios.set(this.fillDays(t.horarios ?? []));
                } else {
                    this.horarios.set(this.fillDays([]));
                }
                this.loading.set(false);
            },
            error: () => {
                this.horarios.set(this.fillDays([]));
                this.loading.set(false);
                this.alerts.error('No se pudo cargar el taller.');
            }
        });
    }

    diaNombre(dia: number) {
        return DIAS[dia - 1] ?? `Día ${dia}`;
    }

    mapaValue(): MapPickerResult | undefined {
        if (this.latitud() == null || this.longitud() == null) {
            return undefined;
        }
        return { lat: Number(this.latitud()), lng: Number(this.longitud()), address: this.direccion() || undefined };
    }

    onTallerLocation(result: MapPickerResult) {
        if (result.address) {
            this.direccion.set(result.address);
        }
        this.latitud.set(result.lat);
        this.longitud.set(result.lng);
    }

    toInput(value: string | null) {
        return value ? value.slice(0, 5) : '';
    }

    toggleCerrado(h: HorarioApi) {
        this.patchDay(h.diaSemana, { esCerrado: !h.esCerrado });
    }

    setHora(h: HorarioApi, field: 'horaInicio' | 'horaFin', value: string) {
        this.patchDay(h.diaSemana, { [field]: value ? `${value}:00` : null });
    }

    guardarTaller() {
        this.savingTaller.set(true);
        this.empresaService.updateTaller({
            nombre: this.nombre(),
            direccion: this.direccion(),
            distrito: this.distrito(),
            telefono: this.telefono(),
            latitud: this.latitud() === null || this.latitud() === undefined ? null : Number(this.latitud()),
            longitud: this.longitud() === null || this.longitud() === undefined ? null : Number(this.longitud()),
            urlMaps: this.urlMaps()
        }).subscribe({
            next: (ok) => {
                this.savingTaller.set(false);
                ok ? this.alerts.ok('Taller actualizado') : this.alerts.error('No se pudo guardar el taller');
            },
            error: () => {
                this.savingTaller.set(false);
                this.alerts.error('No se pudo guardar el taller');
            }
        });
    }

    guardarTodo() {
        if (this.guardandoTodo() || this.horarios().length === 0) {
            return;
        }
        this.guardandoTodo.set(true);
        forkJoin(this.horarios().map((h) => this.empresaService.updateHorario({
            uuid: h.uuid || null,
            diaSemana: h.diaSemana,
            horaInicio: h.esCerrado ? null : h.horaInicio,
            horaFin: h.esCerrado ? null : h.horaFin,
            esCerrado: h.esCerrado
        }))).subscribe({
            next: (resultados) => {
                this.guardandoTodo.set(false);
                resultados.every((ok) => ok) ? this.alerts.ok('Horario guardado') : this.alerts.error('Algunos días no se pudieron guardar');
            },
            error: () => {
                this.guardandoTodo.set(false);
                this.alerts.error('No se pudo guardar el horario');
            }
        });
    }

    guardarDia(h: HorarioApi) {
        this.empresaService.updateHorario({
            uuid: h.uuid || null,
            diaSemana: h.diaSemana,
            horaInicio: h.esCerrado ? null : h.horaInicio,
            horaFin: h.esCerrado ? null : h.horaFin,
            esCerrado: h.esCerrado
        }).subscribe({
            next: (ok) => ok ? this.alerts.ok(`${this.diaNombre(h.diaSemana)} guardado`) : this.alerts.error('No se pudo guardar el horario'),
            error: () => this.alerts.error('No se pudo guardar el horario')
        });
    }

    private fillDays(existing: HorarioApi[]): HorarioApi[] {
        return [1, 2, 3, 4, 5, 6, 7].map((dia) => {
            const found = existing.find((h) => h.diaSemana === dia);
            return found ?? { uuid: '', diaSemana: dia, horaInicio: '08:00:00', horaFin: '18:00:00', esCerrado: dia === 7 };
        });
    }

    private patchDay(dia: number, patch: Partial<HorarioApi>) {
        this.horarios.update((list) => list.map((h) => h.diaSemana === dia ? { ...h, ...patch } : h));
    }
}
